import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Mention, MentionsInput } from "react-mentions";
import Linkify from "linkify-react";
import "linkify-plugin-hashtag"; // Plugin para detectar hashtags
import { neutrals, brand } from "../theme/theme"; // Importar colores neutrals
import { Avatar, AvatarFallback } from "./Avatar";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { useScopedDisabled } from "../hooks/useClientReadOnly";

// Helper para mapear roles del backend a nombres en español
export const getRoleDisplayName = (role) => {
  switch (role) {
    case "consultant":
      return "Consultor";
    case "agency_admin":
      return "Administrador";
    case "superadmin":
      return "Super admin";
    case "client":
      return "Cliente";
    default:
      return role || "Usuario";
  }
};

// Componente personalizado para MentionsInput con estilo Material-UI
export const CustomMentionsInput = ({
  value,
  onChange,
  placeholder,
  onKeyDown,
  onFocus,
  onBlur,
  onPaste,
  users = [],
  sx = {},
  disabled: disabledProp,
  variant = "default", // "default" para comentarios, "composer" para mensajes principales
  /**
   * Cuando es true, aplica estilos/hover/focus/disabled equivalentes a `CustomTextArea`
   * (pensado para `src/Pages/Incidents/ChatInterface.jsx`).
   *
   * Por defecto es false para NO afectar a `ThreadPost.jsx`, `ThreadComposer.jsx`, etc.
   */
  useIncidentsChatStyle = false,
  suggestionsZIndex = 1500,
  /**
   * Cuando es true, renderiza el dropdown de sugerencias en un portal (document.body)
   * para que escape de contenedores con overflow:hidden (p.ej. dentro de modales).
   */
  suggestionsPortal = false,
  forceSuggestionsAboveCursor = false,
  ...props
}) => {
  const disabled = useScopedDisabled(disabledProp);
  const theme = useTheme();
  const { theme: customTheme, colors: customColors } = useCustomTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Portal container para suggestions (escapa de overflow:hidden en modales)
  const portalRef = useRef(null);
  useEffect(() => {
    if (!suggestionsPortal) return;
    const el = document.createElement("div");
    el.setAttribute("data-mentions-portal", "true");
    el.style.position = "absolute";
    el.style.zIndex = String(suggestionsZIndex);
    el.style.top = "0";
    el.style.left = "0";
    document.body.appendChild(el);
    portalRef.current = el;
    return () => {
      document.body.removeChild(el);
      portalRef.current = null;
    };
  }, [suggestionsPortal, suggestionsZIndex]);

  const {
    maxHeight: inputMaxHeight,
    overflow: _overflow,
    overflowY: _overflowY,
    overflowX: _overflowX,
    ...containerSx
  } = sx || {};

  // Mismo tamaño que el textarea móvil forzado en index.css, o el highlighter se desalinea
  const resolvedFontSize = isMobile
    ? "16px"
    : containerSx.fontSize || (variant === "composer" ? "0.875rem" : "14px");
  const resolvedMinHeight =
    containerSx.minHeight ||
    (useIncidentsChatStyle
      ? "40px" // una línea, alineada con los botones del chat de Incidents
      : variant === "composer"
        ? "80px"
        : "60px");
  const resolvedMaxHeight = inputMaxHeight || null;

  const incidentsStyle = useMemo(() => {
    if (!useIncidentsChatStyle) return null;

    const palette = customTheme?.palette ?? {};
    const borderPalette = palette.border ?? {};
    const textPalette = palette.text ?? {};
    const defaultBorder = borderPalette.input ?? palette.divider ?? "#d9d9d9";
    const hoverBorder = borderPalette.default ?? palette.divider ?? "#bdbdbd";
    const focusBorder = "#000000"; // foco neutro en vez del color de marca
    const backgroundDefault = palette.background?.default ?? "#ffffff";
    const disabledBackground =
      palette.action?.disabledBackground ?? backgroundDefault;
    const fontFamily = customTheme?.typography?.fontFamily ?? "inherit";

    const borderColor = disabled
      ? defaultBorder
      : focused
        ? focusBorder
        : hovered
          ? hoverBorder
          : defaultBorder;

    const boxShadow =
      !disabled && focused ? "none" : "none";

    const textColor = disabled
      ? textPalette.disabled ?? "#9e9e9e"
      : textPalette.primary ?? "#1f1f1f";

    return {
      borderColor,
      boxShadow,
      textColor,
      backgroundColor: disabled ? disabledBackground : backgroundDefault,
      fontFamily,
    };
  }, [
    useIncidentsChatStyle,
    customTheme,
    customColors,
    disabled,
    focused,
    hovered,
  ]);

  const mentionStyle = useMemo(() => {
    // Usar siempre "white" para que el sistema de overlay de react-mentions funcione correctamente.
    // Las menciones se ven con el color primario gracias al highlighter overlay.
    return {
      color: "white",
      fontWeight: "normal",
      backgroundColor: "transparent",
      zIndex: 1000000,
    };
  }, []);

  // Estilos simplificados para el input de menciones
  const mentionsInputStyle = {
    control: {
      backgroundColor: "transparent",
      fontSize: resolvedFontSize,
      fontWeight: "normal",
      width: "100%",
      minWidth: 0,
    },
    "&multiLine": {
      control: {
        fontFamily: useIncidentsChatStyle
          ? incidentsStyle?.fontFamily || theme.typography.fontFamily
          : theme.typography.fontFamily,
        minHeight: resolvedMinHeight,
        ...(resolvedMaxHeight && { maxHeight: resolvedMaxHeight }),
        overflowY: resolvedMaxHeight ? "hidden" : "visible",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        border: useIncidentsChatStyle
          ? `1px solid ${incidentsStyle?.borderColor || theme.palette.divider}`
          : variant === "composer"
            ? "none"
            : `1px solid ${theme.palette.divider}`,
        borderRadius: useIncidentsChatStyle
          ? "8px"
          : variant === "composer"
            ? "0px"
            : "8px",
        padding: "0px",
        backgroundColor: useIncidentsChatStyle
          ? incidentsStyle?.backgroundColor || "transparent"
          : "transparent",
        position: "relative",
        transition: useIncidentsChatStyle ? "all 0.2s ease" : undefined,
        boxShadow: useIncidentsChatStyle ? incidentsStyle?.boxShadow : undefined,
      },
      highlighter: {
        padding: useIncidentsChatStyle
          ? "8px 12px"
          : variant === "composer"
            ? "0px 2px"
            : "12px",
        border: "none",
        minHeight: resolvedMinHeight,
        ...(resolvedMaxHeight && { maxHeight: resolvedMaxHeight }),
        overflow: "hidden",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        boxSizing: "border-box",
      },
      input: {
        padding: useIncidentsChatStyle
          ? "8px 12px"
          : variant === "composer"
            ? "0px 2px"
            : "12px",
        margin: 0,
        border: "none",
        outline: "none",
        fontFamily: useIncidentsChatStyle
          ? incidentsStyle?.fontFamily || theme.typography.fontFamily
          : theme.typography.fontFamily,
        fontSize: resolvedFontSize,
        lineHeight: useIncidentsChatStyle ? 1.4 : 1.5,
        resize: "none",
        minHeight: resolvedMinHeight,
        ...(resolvedMaxHeight && { maxHeight: resolvedMaxHeight }),
        color: useIncidentsChatStyle
          ? incidentsStyle?.textColor || theme.palette.text.primary
          : theme.palette.text.primary,
        backgroundColor: "transparent",
        overflowY: "auto",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        boxSizing: "border-box",
      },
    },
    suggestions: {
      list: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        fontSize: "14px",
        borderRadius: "8px",
        boxShadow: theme.shadows[8],
        maxHeight: "200px",
        overflow: "auto",
        /* Higher z-index to ensure the dropdown appears over other UI */
        zIndex: suggestionsZIndex,
        margin: 0,
        padding: 0,
        listStyle: "none",
      },
      item: {
        padding: "0px",
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: "transparent",
        "&focused": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
  };

  return (
    <Box
      sx={{ width: "100%", minWidth: 0, ...containerSx }}
      onMouseEnter={() => {
        if (!disabled) setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onPasteCapture={onPaste}
    >
      <MentionsInput
        value={value}
        onChange={onChange}
        style={mentionsInputStyle}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        onFocus={(e) => {
          if (!disabled) {
            setFocused(true);
            setHovered(false);
          }
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          setHovered(false);
          onBlur && onBlur(e);
        }}
        disabled={disabled}
        a11ySuggestionsListLabel="Usuarios sugeridos"
        allowSuggestionsAboveCursor={true}
        forceSuggestionsAboveCursor={forceSuggestionsAboveCursor}
        {...(suggestionsPortal && portalRef.current
          ? { suggestionsPortalHost: portalRef.current }
          : {})}
        {...props}
      >
        <Mention
          trigger="@"
          data={users}
          style={mentionStyle}
          renderSuggestion={(
            suggestion,
            search,
            highlightedDisplay,
            index,
            focused
          ) => (
            <Box
              sx={{
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                backgroundColor: focused
                  ? theme.palette.action.hover
                  : "transparent",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Avatar
                src={
                  suggestion.profileImage ||
                  suggestion.profile_image ||
                  suggestion.avatar ||
                  suggestion.avatar_url ||
                  suggestion.image ||
                  suggestion.image_url ||
                  undefined
                }
                sx={{ width: 24, height: 24 }}
              >
                <AvatarFallback
                  sx={{
                    backgroundColor: brand.avatar,
                    color: "#fff",
                    fontSize: "0.6rem",
                    fontWeight: 600,
                  }}
                >
                  {suggestion.display
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) || "U"}
                </AvatarFallback>
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {highlightedDisplay}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {suggestion.role || "Usuario"}
                </Typography>
              </Box>
            </Box>
          )}
          displayTransform={(id, display) => `@${display}`}
          markup="@[__display__](user:__id__)"
          appendSpaceOnAdd={true}
        />
      </MentionsInput>
    </Box>
  );
};

// Helper para crear lista de usuarios para menciones
export const createMentionUsers = (
  consultants = [],
  allUsers = [],
  clientData = null,
  currentUser = null
) => {
  const users = [];

  const getProfileImage = (u) =>
    u?.profile_image ||
    u?.profileImage ||
    u?.avatar ||
    u?.avatar_url ||
    u?.image ||
    u?.image_url ||
    null;

  // Agregar consultants
  if (consultants && Array.isArray(consultants)) {
    consultants.forEach((consultant) => {
      if (consultant.id && consultant.full_name) {
        users.push({
          id: consultant.id.toString(),
          display: consultant.full_name,
          role: getRoleDisplayName(consultant.role || "consultant"),
          profileImage: getProfileImage(consultant),
        });
      }
    });
  }

  // Agregar participantes del cliente
  if (clientData?.participants && Array.isArray(clientData.participants)) {
    clientData.participants.forEach((participant) => {
      // Evitar duplicados
      if (
        participant.id &&
        participant.full_name &&
        !users.find((u) => u.id === participant.id.toString())
      ) {
        users.push({
          id: participant.id.toString(),
          display: participant.full_name,
          role: getRoleDisplayName(participant.role || "user"),
          profileImage: getProfileImage(participant),
        });
      }
    });
  }

  // Agregar otros usuarios si están disponibles
  if (allUsers && Array.isArray(allUsers)) {
    allUsers.forEach((user) => {
      // Evitar duplicados
      if (
        user.id &&
        user.full_name &&
        !users.find((u) => u.id === user.id.toString())
      ) {
        users.push({
          id: user.id.toString(),
          display: user.full_name,
          role: getRoleDisplayName(user.role || "user"),
          profileImage: getProfileImage(user),
        });
      }
    });
  }

  // Si no hay usuarios disponibles, al menos incluir al usuario actual
  if (users.length === 0 && currentUser) {
    users.push({
      id: currentUser.id.toString(),
      display: currentUser.name || currentUser.full_name || "Usuario Actual",
      role: getRoleDisplayName(currentUser.role || "user"),
      profileImage: getProfileImage(currentUser),
    });
  }

  return users;
};

// Helper para procesar y mostrar menciones en contenido existente
export const formatContentWithMentions = (content, theme) => {
  // Procesar menciones en formato @[Display Name](user:123)
  const mentionRegex = /@\[([^\]]+)\]\(user:(\d+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  // Opciones de Linkify con detección de hashtags
  const linkifyOptions = {
    className: "linkified-text",
    target: "_blank",
    rel: "noopener noreferrer",
    formatHref: {
      hashtag: (val) => `/hashtag/${val.substring(1)}`,
    },
    render: ({ attributes, content }) => {
      const href = attributes.href || "";

      // Detectar si es un hashtag - renderizar como span NO clicable
      if (href.startsWith("/hashtag/")) {
        return React.createElement(
          "span",
          {
            style: {
              color: neutrals.gray[500], // Color neutral para hashtags
              fontWeight: 500,
              textDecoration: "none",
              cursor: "default",
            },
          },
          content
        );
      }

      // URLs normales - renderizar como link clicable
      return React.createElement(
        "a",
        {
          ...attributes,
          style: {
            color: "#2563eb", // Blue color for links
            textDecoration: "underline",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          },
        },
        content
      );
    },
  };

  while ((match = mentionRegex.exec(content)) !== null) {
    // Agregar texto antes de la mención (con Linkify para detectar URLs y hashtags)
    if (match.index > lastIndex) {
      const textBeforeMention = content.slice(lastIndex, match.index);
      parts.push(
        React.createElement(
          Linkify,
          {
            key: `link-${lastIndex}`,
            options: linkifyOptions,
          },
          textBeforeMention
        )
      );
    }

    // Agregar la mención formateada
    const displayName = match[1];
    const userId = match[2];

    parts.push(
      React.createElement(
        "span",
        {
          key: `mention-${userId}-${match.index}`,
          style: {
            color: theme.palette.primary.main,
            fontWeight: 600,
            cursor: "pointer",
          },
          onClick: (e) => {
            e.preventDefault();
            // Aquí podrías implementar navegación al perfil del usuario
            console.log(`Clicked on user ${displayName} (ID: ${userId})`);
          },
        },
        `@${displayName}`
      )
    );

    lastIndex = match.index + match[0].length;
  }

  // Agregar texto restante después de la última mención (con Linkify)
  if (lastIndex < content.length) {
    const remainingText = content.slice(lastIndex);
    parts.push(
      React.createElement(
        Linkify,
        {
          key: `link-${lastIndex}`,
          options: linkifyOptions,
        },
        remainingText
      )
    );
  }

  return parts.length > 0 ? parts : [content];
};

// Helper para validar si hay contenido válido (texto o menciones)
export const hasValidContent = (content) => {
  const plainText = content.replace(/@\[([^\]]+)\]\(user:\d+\)/g, "").trim();
  const hasMentions = /@\[([^\]]+)\]\(user:\d+\)/g.test(content);
  return plainText.length > 0 || hasMentions;
};
